import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cpu } from 'lucide-react';
import { technologies } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import MouseFollower from '../components/MouseFollower';

const TechnologiesPage = () => {
  const { themeColor } = useTheme();
  const containerRef = useRef(null);

  // Group technologies by category
  const groupedTech = technologies.reduce((acc, tech) => {
    if (!acc[tech.category]) acc[tech.category] = [];
    acc[tech.category].push(tech);
    return acc;
  }, {});

  return (
    <div className="min-h-screen pt-24 pb-16">
      <MouseFollower />
      
      {/* Background */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-5 pointer-events-none"
        style={{ backgroundColor: themeColor.primary }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium"
            style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.1)`, color: themeColor.primary }}
          >
            <Cpu size={16} />
            Tech Stack
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Teknolojilerim
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Projelerimde kullandığım ve uzmanlaştığım teknolojiler
          </p>
        </motion.div>

        {/* Categories */}
        <div className="space-y-16" ref={containerRef}>
          {Object.entries(groupedTech).map(([category, techs], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.2 }}
            >
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                <span
                  className="w-8 h-1 rounded-full"
                  style={{ backgroundColor: themeColor.primary }}
                />
                {category}
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {techs.map((tech, index) => (
                  <motion.div
                    key={tech.name}
                    className="group relative"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: categoryIndex * 0.2 + index * 0.05 }}
                    whileHover={{ y: -8, scale: 1.05 }}
                  >
                    <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700 transition-all text-center">
                      {/* Icon */}
                      <div
                        className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-3xl transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${tech.color}20` }}
                      >
                        {getTechEmoji(tech.icon)}
                      </div>

                      {/* Name */}
                      <h3 className="text-white font-medium mb-2">{tech.name}</h3>

                      {/* Level Bar */}
                      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: tech.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: `${tech.level}%` }}
                          transition={{ duration: 1, delay: 0.2 }}
                          viewport={{ once: true }}
                        />
                      </div>
                      <span className="text-xs text-zinc-500 mt-1 block">{tech.level}%</span>
                    </div>

                    {/* Glow effect on hover */}
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity blur-xl"
                      style={{ backgroundColor: tech.color }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          className="mt-20 p-8 rounded-3xl bg-zinc-900/50 border border-zinc-800/50 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <h3 className="text-2xl font-bold text-white mb-4">
            Sürekli Öğreniyorum
          </h3>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-6">
            Teknoloji dünyası hızla değişiyor ve ben de bu değişime ayak uydurmak için
            sürekli yeni teknolojiler öğreniyorum. Şu anda React Native, Three.js ve
            yapay zeka teknolojileri üzerinde çalışıyorum.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {['React Native', 'Three.js', 'AI/ML', 'Docker', 'GraphQL'].map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 rounded-xl text-sm font-medium border"
                style={{ 
                  borderColor: `rgba(${themeColor.rgb}, 0.3)`,
                  color: themeColor.primary,
                  backgroundColor: `rgba(${themeColor.rgb}, 0.1)`
                }}
              >
                {tech} 📚
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Helper function for tech emojis/icons
const getTechEmoji = (icon) => {
  const emojis = {
    html: '🌐',
    css: '🎨',
    javascript: '✨',
    typescript: '📘',
    react: '⚛️',
    nextjs: '▲',
    tailwind: '💨',
    bootstrap: '🎯',
    python: '🐍',
    php: '🐘',
    mongodb: '🍃',
    mysql: '🗄️',
  };
  return emojis[icon] || '⭐';
};

export default TechnologiesPage;
